import { type ReactNode, useState } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  Skeleton,
  formatDate,
  formatMoney,
  type DataTableColumn,
} from '@aps/ui';
import { type AnalyticsResponse, type Locale } from '@aps/contracts';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

const PERIODS = [7, 30, 90] as const;

/**
 * Analitika — QAROR uchun.
 *
 * Uchta savolga javob beradi: qayerda do'kon ochish, nimani sotib
 * olish, qaysi mashinalarga tovar kerak. Boshqa raqam qo'shilmaydi —
 * qaror qildirmaydigan grafik ekranni to'ldiradi va muhimini
 * yashiradi.
 *
 * UCHTA EKRAN, BITTA SO'ROV. Demoda menyuda uchta alohida punkt bor
 * (qidiruv jurnali, garaj statistikasi, hududlar) va server ham
 * uchala kesimni bitta javobda beradi. Ekranlar bir xil
 * `queryKey` ishlatadi, ya'ni TanStack keshi ularni birlashtiradi:
 * bo'limlar orasida yurganda yangi so'rov ketmaydi va endpoint
 * uchga bo'linmadi.
 */
function useAnalytics(days: number): UseQueryResult<AnalyticsResponse> {
  return useQuery({
    queryKey: ['analytics', days],
    queryFn: () => api.analytics(days),
    placeholderData: keepPreviousData,
  });
}

interface FrameProps {
  title: string;
  days: number;
  onDaysChange: (value: number) => void;
  query: UseQueryResult<AnalyticsResponse>;
  children: (data: AnalyticsResponse) => ReactNode;
}

/** Sarlavha, davr tanlagichi, kutish va xato — uchala ekranda bir xil. */
function AnalyticsFrame({ title, days, onDaysChange, query, children }: FrameProps): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const header = (
    <>
      <header className="adm-page__header">
        <h1 className="adm-page__title">{title}</h1>
      </header>
      <div className="adm-filters__chips">
        {PERIODS.map((value) => (
          <Button
            key={value}
            variant={days === value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              onDaysChange(value);
            }}
          >
            {t.analyticsDays(value)}
          </Button>
        ))}
      </div>
    </>
  );

  if (query.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="260px" label={t.loading} />
      </section>
    );
  }

  if (query.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={query.error instanceof ApiError ? query.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void query.refetch()}
        />
      </section>
    );
  }

  return (
    <section className="adm-page">
      {header}
      {children(query.data)}
    </section>
  );
}

/* ------------------------------ hududlar ------------------------------ */

function regionColumns(
  locale: Locale,
  t: ReturnType<typeof messages>,
): DataTableColumn<AnalyticsResponse['regions'][number]>[] {
  return [
    {
      key: 'name',
      header: t.orderRegion,
      // `nameRu` bo'sh bo'lsa o'zbekchasi ko'rsatiladi — loyiha qoidasi.
      render: (row) =>
        locale === 'RU' ? (row.regionNameRu ?? row.regionNameUz) : row.regionNameUz,
    },
    { key: 'orders', header: t.orders, numeric: true, render: (row) => String(row.orders) },
    {
      key: 'revenue',
      header: t.analyticsRevenue,
      numeric: true,
      render: (row) => formatMoney(row.revenue, locale),
    },
    {
      /*
       * SIGNAL: buyurtma bor, do'kon yo'q — yangi nuqta ochish uchun
       * asos. Shuning uchun bu ustun ajratib ko'rsatiladi.
       */
      key: 'shop',
      header: t.refShops,
      render: (row) =>
        row.hasShop ? (
          <span className="aps-muted">{t.analyticsHasShop}</span>
        ) : (
          <span className="adm-badge adm-badge--pending">{t.analyticsNoShop}</span>
        ),
    },
  ];
}

/**
 * Hududlar bo'yicha.
 *
 * Umumiy sanoq SHU ekranda: hudud jadvalidagi raqamlar nimadan
 * ulush ekanini ko'rsatadi. Qidiruv yoki garaj ekranida u ortiqcha
 * bo'lardi — u yerdagi jadval tushum bilan bog'liq emas.
 */
export function RegionStatsScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [days, setDays] = useState<number>(30);
  const query = useAnalytics(days);

  return (
    <AnalyticsFrame title={t.navRegions} days={days} onDaysChange={setDays} query={query}>
      {(data) => (
        <>
          <section className="adm-panel">
            <h2 className="adm-panel__title">{t.analyticsTotals}</h2>
            <div className="adm-panel__body">
              <div className="adm-kv">
                <span className="adm-kv__key">{t.orders}</span>
                <span className="adm-kv__value">{String(data.totalOrders)}</span>
              </div>
              <div className="adm-kv">
                <span className="adm-kv__key">{t.analyticsRevenue}</span>
                <span className="adm-kv__value">{formatMoney(data.totalRevenue, locale)}</span>
              </div>
            </div>
          </section>

          <section className="adm-panel">
            <h2 className="adm-panel__title">{t.analyticsRegions}</h2>
            <div className="adm-panel__body">
              <p className="aps-field__hint">{t.analyticsRegionsHint}</p>
              <DataTable
                caption={t.analyticsRegions}
                columns={regionColumns(locale, t)}
                rows={data.regions}
                rowKey={(row) => row.regionId}
                empty={<EmptyState title={t.analyticsEmpty} />}
              />
            </div>
          </section>
        </>
      )}
    </AnalyticsFrame>
  );
}

/* ---------------------------- qidiruv jurnali ---------------------------- */

/**
 * Natijasiz qidiruvlar.
 *
 * Bu ekranning qiymati — SOTIB OLISH ro'yxati: odam qidirgan, lekin
 * bizda yo'q. Shuning uchun jadval sanoq bo'yicha tartiblangan
 * holda serverdan keladi.
 */
export function SearchLogScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [days, setDays] = useState<number>(30);
  const query = useAnalytics(days);

  const columns: DataTableColumn<AnalyticsResponse['emptySearches'][number]>[] = [
    { key: 'query', header: t.analyticsQuery, render: (row) => row.queryNorm },
    { key: 'count', header: t.analyticsCount, numeric: true, render: (row) => String(row.count) },
    { key: 'last', header: t.analyticsLastAt, render: (row) => formatDate(row.lastAt, locale) },
  ];

  return (
    <AnalyticsFrame title={t.navSearchLog} days={days} onDaysChange={setDays} query={query}>
      {(data) => (
        <section className="adm-panel">
          <h2 className="adm-panel__title">{t.analyticsEmptySearches}</h2>
          <div className="adm-panel__body">
            <p className="aps-field__hint">{t.analyticsEmptySearchesHint}</p>
            <DataTable
              caption={t.analyticsEmptySearches}
              columns={columns}
              rows={data.emptySearches}
              rowKey={(row) => row.queryNorm}
              empty={<EmptyState title={t.analyticsEmpty} />}
            />
          </div>
        </section>
      )}
    </AnalyticsFrame>
  );
}

/* --------------------------- garaj statistikasi --------------------------- */

/** Mijozlar garajidagi mashinalar — qaysi modelga tovar kerakligi. */
export function GarageStatsScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [days, setDays] = useState<number>(30);
  const query = useAnalytics(days);

  const columns: DataTableColumn<AnalyticsResponse['garage'][number]>[] = [
    { key: 'car', header: t.refVehicles, render: (row) => `${row.makeName} ${row.modelName}` },
    { key: 'count', header: t.analyticsCount, numeric: true, render: (row) => String(row.count) },
  ];

  return (
    <AnalyticsFrame title={t.navGarage} days={days} onDaysChange={setDays} query={query}>
      {(data) => (
        <section className="adm-panel">
          <h2 className="adm-panel__title">{t.analyticsGarage}</h2>
          <div className="adm-panel__body">
            <p className="aps-field__hint">{t.analyticsGarageHint}</p>
            <DataTable
              caption={t.analyticsGarage}
              columns={columns}
              rows={data.garage}
              rowKey={(row) => `${row.makeName}-${row.modelName}`}
              empty={<EmptyState title={t.analyticsEmpty} />}
            />
          </div>
        </section>
      )}
    </AnalyticsFrame>
  );
}
